import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus, EventType } from "@azure/msal-browser";
import { loginRequest } from "../authConfig.jsx";
import { useTranslation } from "../contexts/TranslationContext";
import { ShieldCheckIcon, LockClosedIcon } from "@heroicons/react/24/solid";
import { GlobeAltIcon } from "@heroicons/react/24/outline";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import logo from "../images/logo.png";

/**
 * UserLogin — Standalone login page (no navbar/footer)
 * Accessed via app.cogentplatform.com/login
 */
const UserLogin = () => {
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { instance, accounts, inProgress } = useMsal();
  const { lang, t, changeLanguage } = useTranslation();

  const token = localStorage.getItem('token');
  const role = localStorage.getItem('userRole');

  // Listen for MSAL login failure events
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_FAILURE) {
        console.warn('[MSAL] Login failed/cancelled:', event.error);
        setLoading(false);
        const code = event.error?.errorCode || '';
        if (code === 'user_cancelled' || code === 'access_denied') {
          setError(lang === 'es'
            ? 'Inicio de sesión cancelado. Inténtelo de nuevo.'
            : 'Login was cancelled. Please try again.');
        } else if (code === 'interaction_in_progress') {
          localStorage.clear();
          window.location.replace('/login');
          return;
        }
      }
    });
    return () => instance.removeEventCallback(callbackId);
  }, [instance, lang]);

  // Auto-trigger login from invite email link (?email=...)
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const params = new URLSearchParams(window.location.search);
    const emailHint = params.get("email");
    if (emailHint && accounts.length === 0) {
      setLoading(true);
      const langParam = params.get("lang");
      instance.loginRedirect({
        ...loginRequest,
        prompt: "login",
        loginHint: emailHint,
        ...(langParam && { extraQueryParameters: { ui_locales: langParam, mkt: langParam } }),
      }).catch((err) => {
        console.error("Login redirect error:", err);
        setError("Failed to start login. Please click Sign In below.");
        setLoading(false);
      });
    }
  }, [instance, accounts, inProgress]);

  // Redirect if already authenticated
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (token && role) { navigate('/', { replace: true }); return; }
    if (accounts.length > 0 && !token) { navigate('/', { replace: true }); return; }
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === "verified") {
      setSuccessMessage(t('login.accountVerified'));
    }
  }, [inProgress, token, role, accounts, navigate, t]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");
    setLoading(true);
    try {
      const ciamLocale = lang === 'es' ? 'es-ES' : 'en-US';
      await instance.loginRedirect({
        ...loginRequest,
        prompt: 'login',
        extraQueryParameters: { ui_locales: ciamLocale, mkt: ciamLocale },
      });
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Login failed. Please try again.");
      setLoading(false);
    }
  };

  if (inProgress !== InteractionStatus.None || loading) {
    return (
      <div className="relative min-h-screen">
        <LoadingSpinner variant="page" message={t('login.loggingIn')} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#0f172a' }}>
      {/* Background pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        {/* Subtle gradient orbs */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-cyan-500/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      </div>

      {/* Language switcher — top right */}
      <div className="relative z-10 flex justify-end p-6">
        <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg px-4 py-2.5">
          <GlobeAltIcon className="h-5 w-5 text-white" />
          <select
            value={lang}
            onChange={(e) => changeLanguage(e.target.value)}
            className="bg-transparent text-base text-white font-medium border-none outline-none cursor-pointer appearance-none pr-5"
            style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'14\' height=\'14\' viewBox=\'0 0 12 12\'%3E%3Cpath d=\'M3 5l3 3 3-3\' fill=\'none\' stroke=\'%23ffffff\' stroke-width=\'1.5\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right center' }}
          >
            <option value="en" className="text-gray-900">English</option>
            <option value="es" className="text-gray-900">Espa&ntilde;ol</option>
          </select>
        </div>
      </div>

      {/* Main content — centered */}
      <main className="flex-grow flex items-center justify-center px-4 pb-12 relative z-10 -mt-8">
        <div className="w-full max-w-lg">
          {/* Login card */}
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
            {/* Branded header with logo */}
            <div className="px-10 py-8" style={{ background: 'linear-gradient(135deg, #11253b 0%, #1e4976 50%, #2a5a9e 100%)' }}>
              <div className="flex justify-center mb-4">
                <div className="rounded-xl py-2 px-4" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}>
                  <img src={logo} alt="Cogent Platform" className="h-12" style={{ filter: 'brightness(1.1) contrast(1.05)' }} />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-white text-center">
                {t('login.title')}
              </h1>
              <p className="mt-2 text-base text-blue-200 text-center">
                {t('login.subtitle')}
              </p>
            </div>

            {/* Card body */}
            <div className="px-10 py-8 space-y-6">

              {/* Security info */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <div className="flex items-start">
                  <ShieldCheckIcon className="h-6 w-6 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <p className="text-sm text-gray-700 leading-relaxed">
                    {t('login.securityDescription')}
                  </p>
                </div>
              </div>

              {/* Success Message */}
              {successMessage && (
                <div className="bg-green-50 border border-green-200 text-green-800 p-4 rounded-xl text-sm" role="alert">
                  <p>{successMessage}</p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-800 p-4 rounded-xl text-sm" role="alert">
                  <p>{error}</p>
                </div>
              )}

              {/* Login Button */}
              <form onSubmit={handleLogin}>
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white py-4 rounded-xl text-lg font-semibold hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 flex items-center justify-center"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2" />
                      {t('login.buttonLoading')}
                    </>
                  ) : (
                    <>
                      <LockClosedIcon className="h-5 w-5 mr-2" />
                      {t('login.signInButton')}
                    </>
                  )}
                </button>
              </form>

              {/* Passwordless hint */}
              <p className="text-center text-sm text-gray-500">
                {lang === 'es'
                  ? 'Inicie sesión de forma segura \u2014 verificaremos su identidad a través de su correo electrónico registrado.'
                  : 'Sign in seamlessly \u2014 we\u2019ll verify your identity through your registered email.'}
              </p>
            </div>
          </div>

          {/* Security warning — below card like StashAway */}
          <div className="mt-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4">
            <div className="flex items-start">
              <LockClosedIcon className="h-4 w-4 text-gray-400 mt-0.5 mr-3 flex-shrink-0" />
              <p className="text-xs text-gray-400 leading-relaxed">
                {lang === 'es'
                  ? 'Nunca comparta sus datos de inicio de sesión con nadie. Tenga siempre cuidado con los intentos de phishing.'
                  : 'Never share any of your login details with anyone. Always be aware of phishing attempts when asked for such details.'}
              </p>
            </div>
          </div>

          {/* Copyright */}
          <p className="mt-6 text-center text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Cogent Asset Management Ltd
          </p>
        </div>
      </main>
    </div>
  );
};

export default UserLogin;
